/**
 * Rendering of button-bar controls
 */
import {LayerButtonElement} from "@/event/calendar/CalendarModel.ts";
import {Coordinates, drawWithShadow, fillInnerShadow, IconTextMeasure, measureIconText, Rect, roundFillShape, roundRect} from "@/event/calendar/Shapes.ts";
import {BUTTON_HEIGHT, BUTTON_HPADDING, ICON_FONT} from "@/event/calendar/Constants.ts";
import {BUBBLE, Component, DONE} from "@/event/calendar/dom/Component.ts";
import {DrawingContext} from "@/event/calendar/dom/DrawingContext.ts";


export class ButtonBarComponent extends Component {
	private bar: ButtonBar;
	private measure: ButtonBarMeasure;
	private pointerIn: Coordinates|null;

	constructor(bar:ButtonBar) {
		super();
		this.bar = bar;
		this.pointerIn = null;
	}

	layoutSelf(dc: DrawingContext, contentBox: Rect) {
		const fontFamily = dc.style.getPropertyValue("--font");
		this.measure = calcButtonBar(dc, this.bar, fontFamily, BUTTON_HEIGHT, this.root().width);
		this.width = this.measure.width;
		this.height = this.measure.height;
	}


	protected drawBackground(dc: DrawingContext, area: Rect) {
		const buttonHue = dc.style.getPropertyValue("--button-hue");
		const buttonSat = dc.style.getPropertyValue("--button-sat");
		const buttonLightness = dc.style.getPropertyValue("--button-lightness");

		const buttonBg = "hsl(" + buttonHue + " " + buttonSat + " " + buttonLightness + ")";

		const outer:Rect = { x: -10, y: -10, width: this.width + 20, height: this.height + 20};
		const shape = roundRect({x: 0, y: 0, width: this.width, height: this.height}, 5, 5, 5, 5);


		drawWithShadow(dc.ctx, 2, 2, 8, "#00000044", ctx => {
			ctx.fillStyle = buttonBg;
			ctx.beginPath();
			ctx.roundRect(0, 0, this.width, this.height, 5);
			ctx.fill();
		});

		fillInnerShadow(dc.ctx, outer, shape, -1, -1, 6, "#00000022");
	}

	protected drawBorder(dc: DrawingContext, area: Rect) {
		const buttonFg = dc.style.getPropertyValue("--button-primary-fg");
		const buttonHue = dc.style.getPropertyValue("--button-hue");
		const buttonSat = dc.style.getPropertyValue("--button-sat");
		const buttonLightness = dc.style.getPropertyValue("--button-lightness");

		const buttonBg = "hsl(" + buttonHue + " " + buttonSat + " " + buttonLightness + ")";
		const buttonBgHover = "hsl(" + buttonHue + " " + buttonSat + " 60%)";
		const buttonBorderStyle = "hsl(" + buttonHue + " " + buttonSat + " 40%)";
		const buttonHoverFg = dc.style.getPropertyValue("--button-primary-fg-hover");
		const fontFamily = dc.style.getPropertyValue("--font");

		const h = this.measure.height;
		let sx = 0;
		const btnRadius = 5;

		for (let i = 0; i < this.bar.length; i++) {
			let be = this.bar[i];
			let bm = this.measure.buttons[i];
			const w = bm.width;
			const rect:Rect = { x: sx, y: 0, width: bm.width, height: this.height };	// button rect
			const first = i === 0;
			const last = i === (this.bar.length - 1);
			const radiusTopLeft:number = first ? btnRadius : 0, radiusTopRight:number = last ? btnRadius : 0,
				radiusBottomLeft:number = first ? btnRadius : 0, radiusBottomRight:number = last ? btnRadius : 0;

			dc.ctx.strokeStyle = buttonBorderStyle;
			dc.ctx.fillStyle = buttonBg;

			if (be.hover) {
				roundFillShape(dc.ctx, rect, radiusTopLeft, radiusTopRight, radiusBottomRight, radiusBottomLeft);
				dc.ctx.fillStyle = buttonBgHover;
				dc.ctx.fill();
			}

			// Draw label
			dc.ctx.fillStyle = !!be.hover ? buttonHoverFg : buttonFg;
			let labelStart = sx;
			if (bm.icon) {
				dc.ctx.font = "14px "+ICON_FONT;
				drawWithShadow(dc.ctx, 1, 1, 4, "#00000077", ctx => ctx.fillText(bm.icon, labelStart + bm.iconOffset, this.measure.baseline));
			}
			if (bm.text) {
				dc.ctx.font = "14px "+fontFamily;
				drawWithShadow(dc.ctx, 1, 1, 4, "#00000077", ctx => ctx.fillText(bm.text, labelStart + bm.textOffset, this.measure.baseline));
			}

			if (!first) {
				dc.ctx.strokeStyle = buttonBorderStyle;
				dc.ctx.lineWidth = 0.5;
				dc.ctx.beginPath();
				let x = Math.floor(rect.x)+0.5;	// align to pixel
				dc.ctx.moveTo(x, rect.y + 1);
				dc.ctx.lineTo(x, rect.y + rect.height - 1);
				dc.ctx.stroke();
			}

			sx += w;
		}
		// dc.ctx.strokeStyle = buttonBorderStyle;
		// dc.ctx.lineWidth = 1;
		// dc.ctx.beginPath();
		// dc.ctx.roundRect(0.5, 0.5, this.width - 1, this.height - 1, 5);
		// dc.ctx.stroke();
	}

	changeButtonHoverState(button:LayerButtonElement, hover:boolean):boolean {
		const oldHover = !!button.hover;
		if (oldHover !== hover) {
			button.hover = hover;
			return true;
		}
		return false;
	}

	dumpButtons() {
		let d = "[";
		for (let b of this.bar) {
			d += b.id+"="+b.hover+"  ";
		}
		console.log(d);
	}

	/** Return index of button pointed by pointerIn member property */
	findPointerButton():number {
		let start = 0;
		for (let i = 0; i < this.measure.buttons.length; i++) {
			let bm:ButtonMeasure = this.measure.buttons[i];
			if (this.pointerIn.x >= start && this.pointerIn.x < start + bm.width) return i;
			start += bm.width;
		}
		return -1;
	}

	updateButtonHover(isOver:boolean):boolean {
		let changed:boolean = false;
		let start = 0;
		for (let i = 0; i < this.measure.buttons.length; i++) {
			let bm:ButtonMeasure = this.measure.buttons[i];
			let button = this.bar[i];
			changed = this.changeButtonHoverState(button, isOver && this.pointerIn.x >= start && this.pointerIn.x < start + bm.width) || changed;
			start += bm.width;
		}
		//this.dumpButtons();
		return changed;
	}

	onPointerLeave(e: PointerEvent, x: number, y: number) {
		super.onPointerLeave(e, x, y);
		this.pointerIn = null;
		this.updateButtonHover(false);
		this.dirty(false);
		return true;
	}

    onPointerEnter(e: PointerEvent, x: number, y: number) {
		super.onPointerEnter(e, x, y);
		this.pointerIn = { x, y };
		this.updateButtonHover(true);
		this.dirty(false);
		return true;
	}


	onPointerMove(e: PointerEvent, localX: number, localY: number) {
		this.pointerIn = { x: localX, y: localY };
		if (this.updateButtonHover(true)) this.dirty(false);
		return true;
	}


	onClick(e: MouseEvent, localX: number, localY: number): boolean {
		this.pointerIn = { x: localX, y: localY };
		const idx = this.findPointerButton();
		if (idx >= 0) {
			const button = this.bar[idx];
			e.preventDefault();
			this.fireEvent("action", { id: button.id, shift: e.shiftKey, meta: e.metaKey, alt: e.altKey, ctrl: e.ctrlKey });
			return DONE;
		}
		return BUBBLE;
	}


}


export type ButtonBar = LayerButtonElement[];

export interface ButtonMeasure {
	id: string;
	width: number;	// total width of button
	shape: Path2D;
	iconOffset: number;
	icon: string|null;
	text: string|null;
	textOffset: number;
}
export interface ButtonBarMeasure {
	width: number;		// total width of buttonBar
	height: number;		// total height of buttonBar
	baseline: number;	// position of baseline
	buttons: ButtonMeasure[]
}

function calcButtonBar(dc: DrawingContext, bar: ButtonBar, fontFamily:string, buttonHeight:number, canvasWidth:number = Infinity):ButtonBarMeasure {
	try {
		dc.ctx.save();
		const itemMeasures:IconTextMeasure[] = [];
		let barWidth = 0;
		let maxAscent = 0;
		let maxDescent = 0;
		for (let be of bar) {
			let title = be.title == null ? null : dc.env.msg(be.title);
			if (be.dropLabel && canvasWidth < be.dropLabel && be.icon) title = null;
			let m = measureIconText(dc.ctx, title, be.icon, fontFamily);
			itemMeasures.push(m);
			barWidth += m.width + 2 * BUTTON_HPADDING;
			maxAscent = Math.max(maxAscent, m.ascent);
			maxDescent = Math.max(maxDescent, m.descent);
		}
		const totalTextHeight = maxAscent + maxDescent;
		const baselineTopOffset = (BUTTON_HEIGHT - totalTextHeight) / 2; 	// distance of baseline from top of the buttons

		const bbm:ButtonBarMeasure = {
			width: barWidth,
			height: buttonHeight,
			baseline: Math.floor(baselineTopOffset + maxAscent),
			buttons: [],
		};
		let x = 0;
		for (let i = 0; i < bar.length; i++) {
			let be = bar[i];
			let im = itemMeasures[i];
			const buttonWidth = im.width + 2 * BUTTON_HPADDING
			const btnRect: Rect = { x, y: 0, width: buttonWidth, height: BUTTON_HEIGHT};
			const btnShape = new Path2D();
			btnShape.roundRect(btnRect.x, btnRect.y, btnRect.width, btnRect.height, 5);
			bbm.buttons.push({
				id: be.id,
				width: buttonWidth,
				iconOffset: x + BUTTON_HPADDING,
				textOffset: x + BUTTON_HPADDING + im.iconWidth,
				shape: btnShape,
				icon: im.icon,
				text: im.text
			});
		}
		return bbm;
	} finally {
		dc.ctx.restore();
	}
}
